'use client'

import { useEffect, Suspense, useRef } from 'react'
import { usePathname, useSearchParams } from 'next/navigation'
import { AnalyticsBrowser } from '@customerio/cdp-analytics-browser'

// Initialize Customer.io analytics
export const cioanalytics = AnalyticsBrowser.load(
  { writeKey: process.env.NEXT_PUBLIC_ANALYTICS_WRITE_KEY },
  {
    integrations: {
      "Customer.io In-App Plugin": {
        siteId: process.env.NEXT_PUBLIC_ANALYTICS_SITE_ID,
        anonymousInApp: true,
        events: function (event) {
          switch (event.type) {
            case "in-app:message-opened":
              console.log('Message opened:', event);
              cioanalytics.track('in_app_message_opened', {
                messageId: event.data?.messageId,
                timestamp: new Date().toISOString()
              });
              break;
            case "in-app:message-dismissed":
              console.log('Message dismissed:', event);
              cioanalytics.track('in_app_message_dismissed', {
                messageId: event.data?.messageId,
                timestamp: new Date().toISOString()
              });
              break;
            case "in-app:message-action":
              console.log('Message action:', event);
              cioanalytics.track('in_app_message_action', {
                messageId: event.data?.messageId,
                actionValue: event.data?.actionValue,
                timestamp: new Date().toISOString()
              });
              break;
            case "in-app:message-error":
              console.error('Message error:', event);
              cioanalytics.track('in_app_message_error', {
                messageId: event.data?.messageId,
                error: event.data?.error,
                timestamp: new Date().toISOString()
              });
              break;
          }
        }
      }
    }
  }
)

// Expose cioanalytics to window object for console testing
if (typeof window !== 'undefined') {
  window.cioanalytics = cioanalytics;
}

// Helper function to get cookie value by name
function getCookie(name) {
  if (typeof document === 'undefined') return null;

  const value = `; ${document.cookie}`;
  const parts = value.split(`; ${name}=`);
  if (parts.length === 2) return parts.pop().split(';').shift();
  return null;
}

// Page tracker component
function PageTracker() {
  const pathname = usePathname()
  const searchParams = useSearchParams()
  const identifiedRef = useRef(false)

  useEffect(() => {
    // Check for cio_customer_id cookie
    const customerId = getCookie('cio_customer_id');

    // Reset analytics if the customer ID is empty or missing
    if (!customerId) {
      console.log('No customer ID found, resetting analytics');
      cioanalytics.reset();
      identifiedRef.current = false;
    } else if (!identifiedRef.current) {
      // Identify the customer when the ID is present and we haven't identified yet
      console.log('Customer ID found, identifying customer:', customerId);
      cioanalytics.identify(customerId);
      identifiedRef.current = true;
    }

    // Track page views when path or search params change
    cioanalytics.page();
  }, [pathname, searchParams]);

  return null;
}

// Fallback component for Suspense
function PageTrackerFallback() {
  return null;
}

export function CustomerIO() {
  return (
    <Suspense fallback={<PageTrackerFallback />}>
      <PageTracker />
    </Suspense>
  );
}
